/**
 * DataGrail consent categories
 */
const DataGrailCategory = {
  ESSENTIAL: "dg-category-essential",
  FUNCTIONAL: "dg-category-functional", 
  MARKETING: "dg-category-marketing",
  PERFORMANCE: "dg-category-performance",
};

/**
 * Simple consent manager for DataGrail integration
 * Provides utilities to check consent status and run callbacks when consent changes
 */
class ConsentManager {
  constructor() {
    this.callbacks = new Map(); // categoryId -> array of callbacks
    this.isInitialized = false;
    this.init();
  }

  /**
   * Initialize the consent manager
   */
  init() {
    if (typeof window === "undefined") return;

    // Initialize DataGrail event array early
    window.dgEvent = window.dgEvent || [];

    // Check if DataGrail consent checking is disabled
    if (window.jsConfig && window.jsConfig.datagrailEnabled === false) {
      this.isInitialized = true;
      this.executeAllCallbacks();
      return;
    }

    // Wait for DataGrail banner to load
    if (!window.DG_BANNER_API) {
      const checkDataGrail = () => {
        if (window.DG_BANNER_API) {
          this.setupDataGrailCallbacks();
        } else {
          setTimeout(checkDataGrail, 100);
        }
      };
      checkDataGrail();
      return;
    }

    this.setupDataGrailCallbacks();
  }

  /**
   * Set up DataGrail event callbacks
   */
  setupDataGrailCallbacks() {
    // Set up the initial preference callback
    const initialCallback = (preferences) => {
      this.isInitialized = true;
      this.executeAllCallbacks();
    };

    // Set up the preference change callback
    const preferenceCallback = (preferences) => {
      this.executeAllCallbacks();
    };

    // Register both callbacks
    window.dgEvent.push(
      {
        event: "initial_preference_callback",
        params: initialCallback,
      },
      {
        event: "preference_callback",
        params: preferenceCallback,
      },
    );
  }

  /**
   * Check if a specific consent category is enabled using DataGrail API
   * @param {string} categoryId - The consent category ID to check
   * @returns {boolean} Whether the category is enabled
   */
  checkConsent(categoryId) {
    if (!window.DG_BANNER_API || !window.DG_BANNER_API.getConsentPreferences) {
      return false;
    }

    try {
      const preferences = window.DG_BANNER_API.getConsentPreferences();
      
      // Check if cookieOptions is directly in preferences
      if (preferences && preferences.cookieOptions) {
        const cookieOptions = preferences.cookieOptions;
        const category = cookieOptions.find(option => option.gtm_key === categoryId);
        const isEnabled = category ? category.isEnabled : false;
        return isEnabled;
      }
    } catch (error) {
      console.error("Error checking consent:", error);
    }

    return false;
  }

  /**
   * Execute a callback if consent is granted for a specific category
   * Supports multiple callbacks per category
   * @param {string} categoryId - The consent category ID to check
   * @param {Function} callback - Function to execute if consent is granted
   */
  ifConsent(categoryId, callback) {
    
    if (!this.callbacks.has(categoryId)) {
      this.callbacks.set(categoryId, []);
    }
    this.callbacks.get(categoryId).push(callback);

    // Execute immediately if consent is already granted
    if (this.checkConsent(categoryId)) {
      try {
        callback();
      } catch (error) {
        console.error(`Error executing callback for ${categoryId}:`, error);
      }
    }
  }

  /**
   * Execute a callback when consent manager is ready AND consent is granted for a specific category
   * This combines whenReady() and ifConsent() into a single method
   * @param {string} categoryId - The consent category ID to check
   * @param {Function} callback - Function to execute when ready and consent is granted
   */
  ifConsent(categoryId, callback) {
    
    if (!this.callbacks.has(categoryId)) {
      this.callbacks.set(categoryId, []);
    }
    this.callbacks.get(categoryId).push(callback);

    // Execute immediately if consent manager is ready and consent is granted
    if (this.isInitialized && this.checkConsent(categoryId)) {
      try {
        callback();
      } catch (error) {
        console.error(`Error executing callback for ${categoryId}:`, error);
      }
    }
  }

  /**
   * Execute all callbacks for categories that have consent
   */
  executeAllCallbacks() {
    this.callbacks.forEach((callbacks, categoryId) => {
      if (categoryId === 'ready') {
        // Execute ready callbacks
        callbacks.forEach(callback => {
          try {
            callback();
          } catch (error) {
            console.error("Error executing ready callback:", error);
          }
        });
      } else if (this.checkConsent(categoryId)) {
        // Execute consent callbacks
        callbacks.forEach(callback => {
          try {
            callback();
          } catch (error) {
            console.error(`Error executing callback for ${categoryId}:`, error);
          }
        });
      }
    });
  }
}

// Initialize the global consent manager
if (typeof window !== "undefined") {
  window.consentManager = new ConsentManager();
  window.DataGrailCategory = DataGrailCategory;
  window.DG_CONSENT_UPDATE = window.DG_CONSENT_UPDATE || function(...args) {console.log("DG_CONSENT_UPDATE not set", args)};
}
